import React from "react";
import { makeStyles } from "@mui/styles";
import { useTranslation } from "react-i18next";
import logo from "../assets/img/logo138x75white.png";
import { FONT_FAMILY } from "../common/sharedFunctions";

const GOLD = "#D3730F";
const BACKGROUND = "#050507";
const TEXT = "#FFFFFF";
const MUTED = "rgba(255,255,255,0.7)";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    backgroundColor: BACKGROUND,
    color: TEXT,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "64px 20px 80px",
    fontFamily: FONT_FAMILY,
  },
  header: {
    textAlign: "center",
    marginBottom: "48px",
  },
  logo: {
    width: "220px",
    maxWidth: "70%",
    height: "auto",
    marginBottom: "24px",
  },
  title: {
    margin: "0 0 12px 0",
    color: GOLD,
    fontSize: "28px",
    letterSpacing: "0.4px",
  },
  subtitle: {
    margin: 0,
    color: MUTED,
    fontSize: "16px",
    lineHeight: 1.6,
  },
  container: {
    width: "100%",
    maxWidth: "900px",
  },
  section: {
    marginBottom: "56px",
  },
  sectionTitle: {
    margin: "0 0 24px 0",
    color: GOLD,
    fontSize: "22px",
  },
  sectionHeading: {
    margin: "20px 0 12px 0",
    fontSize: "18px",
    color: GOLD,
  },
  paragraph: {
    margin: "0 0 16px 0",
    lineHeight: 1.7,
    color: TEXT,
    '& strong': {
      color: GOLD,
      fontWeight: 600,
    },
    '& a': {
      color: GOLD,
      textDecoration: "none",
      borderBottom: "1px solid rgba(211,115,15,0.35)",
    },
    '& a:hover': {
      borderBottomColor: "rgba(211,115,15,0.65)",
    },
  },
  separator: {
    height: "1px",
    border: 0,
    background: "rgba(255,255,255,0.08)",
    margin: "24px 0 56px",
  },
  footerNote: {
    marginTop: "24px",
    textAlign: "center",
    fontSize: "13px",
    color: MUTED,
  },
});

export default function TermCondition() {
  const { i18n } = useTranslation();
  const classes = useStyles();
  const direction = i18n.dir();
  const headingAlign = direction === "rtl" ? "right" : "left";
  const year = new Date().getFullYear();

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <img src={logo} alt="Compagnie VTC 56" className={classes.logo} />
        <h1 className={classes.title} style={{ textAlign: headingAlign }}>
          Termes et Conditions / Terms and Conditions
        </h1>
        <p className={classes.subtitle} style={{ textAlign: headingAlign }}>
          Conditions d'utilisation de nos services VTC, transport et livraison.
        </p>
      </header>

      <main
        className={classes.container}
        style={{ direction, textAlign: headingAlign }}
      >
        <section className={classes.section}>
          <h2 className={classes.sectionTitle}>Français</h2>

          <h3 className={classes.sectionHeading}>Termes</h3>
          <p className={classes.paragraph}>
            Merci d'avoir choisi notre service de réservation basés sur des applications pour les besoins de VTC, de VTC et de livraison. Veuillez lire attentivement les termes et conditions suivants avant d'utiliser nos services:
          </p>

          <p className={classes.paragraph}>
            <strong>Acceptation des termes:</strong> En utilisant nos services, vous acceptez d'être lié par ces termes et conditions. Si vous n'acceptez pas ces termes et conditions, vous ne pouvez pas utiliser nos services.
          </p>

          <p className={classes.paragraph}>
            <strong>Éligibilité:</strong> Vous devez avoir au moins 18 ans pour utiliser nos services. En utilisant nos services, vous déclarez et garantissez que vous avez au moins 18 ans.
          </p>

          <p className={classes.paragraph}>
            <strong>Description du service:</strong> Nos services de réservation basés sur des applications fournissent une plate-forme pour connecter les utilisateurs avec des prestataires de VTC, de VTC et de livraison. Nous ne possédons pas et ne contrôlons aucun des véhicules ou des conducteurs qui utilisent notre plate-forme. Nos services sont destinés à une utilisation personnelle et non commerciale uniquement.
          </p>

          <p className={classes.paragraph}>
            <strong>Paiement :</strong> Les utilisateurs doivent payer les services fournis via notre plateforme. Nous acceptons diverses formes de paiement, y compris les cartes de crédit, les cartes de débit et les méthodes de paiement électronique. Tous les paiements effectués via notre plateforme sont soumis à nos politiques de paiement, qui peuvent être mises à jour de temps à autre.
          </p>

          <p className={classes.paragraph}>
            <strong>Conduite des utilisateurs:</strong> Les utilisateurs doivent utiliser nos services de manière responsable et ne pas adopter un comportement pouvant nuire à notre plate-forme, à nos chauffeurs ou à d'autres utilisateurs. L'utilisation de notre plate-forme à des fins illégales ou non autorisées est interdite, tout comme toute action qui compromettrait sa sécurité ou son intégrité.
          </p>

          <p className={classes.paragraph}>
            <strong>Contenu utilisateur:</strong> Les utilisateurs sont seuls responsables du contenu qu'ils soumettent via notre plateforme. En soumettant du contenu, les utilisateurs nous accordent une licence non exclusive, libre de droits, transférable et sous-licenciable pour utiliser, modifier et reproduire ce contenu dans le cadre de la fourniture de nos services.
          </p>

          <p className={classes.paragraph}>
            <strong>Propriété intellectuelle:</strong> Notre plateforme, y compris tous les contenus et la propriété intellectuelle, appartient à nous et/ou à nos concédants de licence. Les utilisateurs ne peuvent pas copier, modifier, distribuer ou reproduire un contenu ou une propriété intellectuelle sans notre consentement écrit préalable.
          </p>

          <p className={classes.paragraph}>
            <strong>Avertissement:</strong> Nous ne garantissons pas la disponibilité, la fiabilité ou la qualité des services fournis par les conducteurs utilisant notre plateforme. Nous ne sommes pas responsables de toute perte ou dommage pouvant résulter de l'utilisation de nos services. Notre plateforme est fournie « telle quelle » et sans aucune garantie, expresse ou implicite.
          </p>

          <p className={classes.paragraph}>
            <strong>Collecte et utilisation des données de localisation:</strong> Notre application peut collecter et traiter des données de localisation (GPS ou localisation approximative) afin de fournir les services proposés, tels que la mise en relation avec des conducteurs à proximité et l'optimisation des trajets. Ces données sont uniquement utilisées pour le bon fonctionnement de l'application et ne sont pas partagées avec des tiers à des fins publicitaires. Vous pouvez désactiver l'accès à la localisation depuis les paramètres de votre appareil, mais certaines fonctionnalités pourraient alors être indisponibles.
          </p>

          <p className={classes.paragraph}>
            Pour l'application mobile : l'application peut collecter et utiliser des données de localisation afin de proposer des fonctionnalités essentielles telles que la mise en relation avec des conducteurs à proximité, l'optimisation des trajets et le suivi en temps réel. Ces données ne sont utilisées que pour assurer le bon fonctionnement de l'application et ne sont pas partagées avec des tiers à des fins publicitaires. Vous pouvez désactiver l'accès à la localisation depuis les paramètres de votre appareil ; certaines fonctionnalités peuvent alors ne plus être disponibles.
          </p>

          <p className={classes.paragraph}>
            <strong>Limitation de la responsabilité:</strong> En aucun cas, nous ne serons responsables de tout dommage indirect, accessoire, spécial ou consécutif résultant de ou en relation avec l'utilisation de nos services, que nous ayons été informés ou non de la possibilité de tels dommages.
          </p>

          <p className={classes.paragraph}>
            <strong>Résiliation:</strong> Nous nous réservons le droit de résilier l'accès d'un utilisateur à notre plateforme à tout moment, sans préavis et pour quelque raison que ce soit.
          </p>

          <p className={classes.paragraph}>
            <strong>Loi applicable:</strong> Ces termes et conditions sont régis et interprétés conformément aux lois de la juridiction où la société est établie.
          </p>

          <p className={classes.paragraph}>
            <strong>Modifications:</strong> Nous nous réservons le droit de mettre à jour ces termes et conditions à tout moment, sans préavis. Il est conseillé aux utilisateurs de consulter régulièrement ces termes et conditions pour rester informés de tout changement.
          </p>

          <p className={classes.paragraph}>
            Pour toute question concernant ces termes et conditions, veuillez nous contacter à l'adresse suivante : <a href="mailto:compagnie.vtc56@gmail.com">compagnie.vtc56@gmail.com</a>.
          </p>
        </section>

        <hr className={classes.separator} />

        <section className={classes.section}>
          <h2 className={classes.sectionTitle}>English</h2>

          <h3 className={classes.sectionHeading}>Terms</h3>
          <p className={classes.paragraph}>
            Thank you for choosing our app-based booking service for VTC, cab, and delivery needs. Please carefully read the following terms and conditions before using our services:
          </p>

          <p className={classes.paragraph}>
            <strong>Acceptance of Terms:</strong> By using our services, you agree to be bound by these terms and conditions. If you do not accept these terms and conditions, you may not use our services.
          </p>

          <p className={classes.paragraph}>
            <strong>Eligibility:</strong> You must be at least 18 years old to use our services. By using our services, you represent and warrant that you are at least 18 years old.
          </p>

          <p className={classes.paragraph}>
            <strong>Service Description:</strong> Our app-based booking services provide a platform to connect users with VTC, cab, and delivery service providers. We do not own, operate, or control any of the vehicles or drivers using our platform. Our services are intended for personal and non-commercial use only.
          </p>

          <p className={classes.paragraph}>
            <strong>Payment:</strong> Users must pay for services provided through our platform. We accept various forms of payment, including credit cards, debit cards, and electronic payment methods. All payments are subject to our payment policies, which may be updated from time to time.
          </p>

          <p className={classes.paragraph}>
            <strong>User Conduct:</strong> Users should act responsibly and must not engage in behavior that may harm our platform, our drivers, or other users. The platform must not be used for any illegal or unauthorized purpose. Any activity that could compromise the security or integrity of our platform is strictly prohibited.
          </p>

          <p className={classes.paragraph}>
            <strong>User Content:</strong> Users are solely responsible for the content they submit through our platform. By submitting content, users grant us a non-exclusive, royalty-free, transferable, and sublicensable license to use, modify, and reproduce the content for the purpose of providing our services.
          </p>

          <p className={classes.paragraph}>
            <strong>Intellectual Property:</strong> Our platform, including all content and intellectual property, belongs to us and/or our licensors. Users may not copy, modify, distribute, or reproduce any content or intellectual property without our prior written consent.
          </p>

          <p className={classes.paragraph}>
            <strong>Disclaimer:</strong> We do not guarantee the availability, reliability, or quality of services provided by drivers using our platform. We are not responsible for any loss or damage that may result from the use of our services. Our platform is provided "as is" and without any warranties, express or implied.
          </p>

          <p className={classes.paragraph}>
            <strong>Collection and Use of Location Data:</strong> Our application may collect and process location data (GPS or approximate location) to provide the offered services, such as connecting with nearby drivers and optimizing routes. This location data is used solely for the proper functioning of the application and is not shared with third parties for advertising purposes. You can disable location access from your device settings, but some features may then be unavailable.
          </p>

          <p className={classes.paragraph}>
            For the mobile application: the app may collect and use location data to provide essential features such as connecting with nearby drivers, route optimization, and real-time tracking. This data is used solely for the proper functioning of the application and is not shared with third parties for advertising purposes. You may disable location access from your device settings; some features may then no longer be available.
          </p>

          <p className={classes.paragraph}>
            <strong>Limitation of Liability:</strong> In no event shall we be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with the use of our services, whether or not we have been informed of the possibility of such damages.
          </p>

          <p className={classes.paragraph}>
            <strong>Termination:</strong> We reserve the right to terminate user access to our platform at any time, without notice, for any reason whatsoever.
          </p>

          <p className={classes.paragraph}>
            <strong>Governing Law:</strong> These terms and conditions shall be governed by and interpreted in accordance with the laws of the jurisdiction where the company is established.
          </p>

          <p className={classes.paragraph}>
            <strong>Amendments:</strong> We reserve the right to update these terms and conditions at any time, without notice. Users are advised to review these terms and conditions regularly to stay informed of any changes.
          </p>

          <p className={classes.paragraph}>
            If you have any questions or concerns regarding these terms and conditions, please contact us at <a href="mailto:compagnie.vtc56@gmail.com">compagnie.vtc56@gmail.com</a>.
          </p>
        </section>
      </main>

      <div className={classes.footerNote}>
        © {year} Compagnie VTC 56 — Tous droits réservés.
      </div>
    </div>
  );
}
